// Shared helpers for the Stripe checkout return path. The https URLs are
// intercepted natively (iOS Universal Links / Android App Links); the
// articulearn:// scheme is the web return page's bounce back into the app
// (universal links do not trigger on same-domain navigation, so that page
// cannot use an https link).
export const BILLING_RETURN_PATH = "/app/billing-return";
export const BILLING_CANCEL_PATH = "/app/billing-cancel";
export const BILLING_RETURN_APP_URL = "articulearn://app/billing-return";
export const BILLING_CANCEL_APP_URL = "articulearn://app/billing-cancel";

/**
 * Map an incoming appUrlOpen URL (https universal link or articulearn://
 * scheme) to the billing path it represents, or null if it is not a billing
 * link. For custom-scheme URLs the WHATWG parser puts the first path segment
 * in host (articulearn://app/billing-return has host "app" and pathname
 * "/billing-return"), so recompose before matching.
 * @param {string} url
 * @returns {"/app/billing-return"|"/app/billing-cancel"|null}
 */
export function normalizeBillingPath(url) {
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  let path = parsed.pathname;
  if (parsed.protocol === "articulearn:") {
    path = parsed.host ? `/${parsed.host}${parsed.pathname}` : parsed.pathname;
    // Older WebView URL parsers (pre-Interop-2024) leave a non-special scheme
    // authority in the pathname as a leading "//app/..." with an empty host;
    // collapse it to a single leading slash so the exact match still works.
    if (path.startsWith("//")) {
      path = path.slice(1);
    }
  }
  // Exact match (allowing a trailing slash). Query and hash are already
  // stripped by the URL parser, so startsWith would only over-match future
  // sibling paths like /app/billing-return-failure.
  if (path === BILLING_RETURN_PATH || path === `${BILLING_RETURN_PATH}/`) {
    return BILLING_RETURN_PATH;
  }
  if (path === BILLING_CANCEL_PATH || path === `${BILLING_CANCEL_PATH}/`) {
    return BILLING_CANCEL_PATH;
  }
  return null;
}

// Stripe lookup keys are short kebab-case slugs (e.g. "fluency-month"). The
// shape check is deliberately conservative: anything else (garbage, injection
// attempts, duplicated params surfacing as arrays upstream) is treated as
// absent, which fails closed — the entitlement watch simply doesn't start.
const PLAN_SHAPE = /^[a-z0-9_-]{1,80}$/i;

/**
 * Parse an incoming appUrlOpen URL into its billing path and the plan the
 * return link carries. The backend puts the purchased lookupKey on the native
 * success URL (?plan=) so the app can re-arm a plan-scoped entitlement watch
 * even when iOS terminated the suspended app during external-browser checkout
 * and every other copy of that state is gone. The WHATWG parser exposes the
 * query for the articulearn:// non-special scheme too, so both URL forms
 * (https universal link, scheme bounce) resolve identically.
 * @param {string} url
 * @returns {{path: "/app/billing-return"|"/app/billing-cancel"|null, plan: string|null}}
 */
export function parseBillingLink(url) {
  const path = normalizeBillingPath(url);
  if (path !== BILLING_RETURN_PATH) {
    return { path, plan: null };
  }
  let plan = null;
  try {
    plan = new URL(url).searchParams.get("plan");
  } catch {
    // normalizeBillingPath already parsed this URL, so this is unreachable in
    // practice; treat as no plan.
  }
  if (!plan || !PLAN_SHAPE.test(plan)) {
    return { path, plan: null };
  }
  return { path, plan };
}
